import React, { ReactElement, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useMount } from 'react-use';
import { Column } from '@zeroedin-tech/zi-common-ui/lib/';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib/components/toast/ToastProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/pro-regular-svg-icons';
import {
	AlertVariant,
	FormGroup,
	Grid,
	GridItem,
	Modal,
	ModalVariant,
} from '@patternfly/react-core';
import { SubheaderContext } from '../layout/Layout';
import PageTitleSubheader from '../layout/subheader/PageTitleSubheader';
import FilterTableLayout from '../layout/FilterTableLayout';
import { unixToHumanTimestamp } from '../helper/Helpers';
import { DummyPermission, useIsGranted } from '../helper/PermissionOverride';
import ZiTable, { Action } from '@zeroedin-react/zi-common/lib/components/table/ZiTable';
import { ActivityLog, TActivityLog } from '../api/logs/ActivityLogs';

export default function ActivityLogs(): ReactElement {
	const { addToast } = useToast();
	const [setSubheader]: SubheaderContext = useOutletContext();
	const [tableData, setTableData] = useState<TActivityLog[]>([]);
	const [filteredTableData, setFilteredTableData] = useState<TActivityLog[]>([]);
	const [tableLoading, setTableLoading] = useState<boolean>(true);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [activeLog, setActiveLog] = useState<TActivityLog>(ActivityLog.Default());
	const [isFormLoading, setIsFormLoading] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>();

	const selectedColumns: Column<TActivityLog>[] = [
		{
			title: 'Log Date',
			columnName: 'created_at',
			customAccessor(row) {
				if (row.created_at) return unixToHumanTimestamp(row.created_at, true);

				return row?.created_at;
			},
			sortable: true,
		},
		{
			title: 'Activity Type',
			columnName: 'activity_type',
			sortable: true,
		},
		{
			title: 'User Account',
			columnName: 'initiator_id',
			customAccessor(row) {
				return row.user?.account ?? 0;
			},
			sortable: true,
		},
		{
			title: 'Message',
			columnName: 'message',
			sortable: true,
		},
		{
			title: 'Entity ID',
			columnName: 'entity_id',
		},
		{
			title: 'Entity Type',
			columnName: 'entity_type',
		},
	];

	const actions: Action<TActivityLog, typeof DummyPermission>[] = [
		{
			name: (
				<>
					<span className="hidden">Edit</span>
					View <FontAwesomeIcon icon={faEye} />
				</>
			),
			callback: (item) => {
				setActiveLog(item);
				setIsModalOpen(true);
			},
			permission: DummyPermission.EDIT,
		},
	];

	useMount(() => {
		setSubheader(
			<PageTitleSubheader
				pageTitle="Activity Logs"
				pageDescription="Manage Activity Logs."
			/>
		);

		ActivityLog.GetAll(['user'])
			.then((response) => {
				setTableData(response.sort((a, b) => b.created_at - a.created_at));
				setFilteredTableData(response.sort((a, b) => b.created_at - a.created_at));
			})
			.catch(() => {
				addToast('Failed to load Activity Logs.', AlertVariant.danger);
			})
			.finally(() => {
				setTableLoading(false);
			});
	});

	const handleClose = () => {
		setIsModalOpen(false);
	};

	const entityTable = (
		<ZiTable<TActivityLog, typeof DummyPermission>
			ariaLabel={'Activity Logs'}
			columns={selectedColumns}
			data={filteredTableData}
			caption="Activity Logs"
			actions={actions}
			loading={tableLoading}
			useIsGranted={useIsGranted}
			Permission={DummyPermission}
		/>
	);

	return (
		<React.Fragment>
			<Modal
				variant={ModalVariant.large}
				title="Activty Log Detail"
				isOpen={isModalOpen}
				onClose={handleClose}
			>
				<FormGroup>
					<Grid hasGutter>
						<GridItem span={6}>
							<b>Created Date</b>
							<div>{unixToHumanTimestamp(activeLog.created_at, true)}</div>
						</GridItem>
						<GridItem span={6}>
							<b>Activity Type</b>
							<div>{activeLog.activity_type}</div>
						</GridItem>
					</Grid>
					<br />
					<Grid hasGutter>
						<GridItem span={6}>
							<b>User Account</b>
							<div>{activeLog.user?.account}</div>
						</GridItem>
						<GridItem span={6}>
							<b>Message</b>
							<div>{activeLog.message}</div>
						</GridItem>
					</Grid>
					<br />
					<Grid hasGutter>
						<GridItem span={6}>
							<b>Entity ID</b>
							<div>{activeLog.entity_id}</div>
						</GridItem>
						<GridItem span={6}>
							<b>Entity Type</b>
							<div>{activeLog.entity_type}</div>
						</GridItem>
					</Grid>
					<br />
					<Grid hasGutter>
						<GridItem span={6}>
							<b>Ip Address</b>
							<div>{activeLog.ip_address}</div>
						</GridItem>
						<GridItem span={6}>
							<b>Context</b>
							<div>{activeLog.context}</div>
						</GridItem>
					</Grid>
				</FormGroup>
			</Modal>
			<React.Fragment>
				<FilterTableLayout table={entityTable} />
			</React.Fragment>
		</React.Fragment>
	);
}
